import { collection, doc, getDocs, query, setDoc, where, updateDoc, getDoc, addDoc, deleteDoc, deleteField, serverTimestamp } from 'firebase/firestore'
import { getFunctions, httpsCallable } from 'firebase/functions'
import { signInWithCustomToken } from 'firebase/auth'
import { firestoreDb, auth } from '../firebase'
import { IUser, IWedding, IWeddingData, IGuest, ISeatingPlan, IPlanner, IPozivnicaPodaci, IPozivnicaDogadjaj } from '../interface'

// Helper to convert Firestore Timestamps or Date objects to ISO strings for Redux serialization
const toISOString = (value: any): string => {
	if (!value) return new Date().toISOString()
	// Firestore Timestamp (has .seconds and .nanoseconds)
	if (value.seconds !== undefined) return new Date(value.seconds * 1000).toISOString()
	// JavaScript Date object
	if (value instanceof Date) return value.toISOString()
	// Already a string (ISO format)
	if (typeof value === 'string') return value
	// Fallback
	return new Date().toISOString()
}

export const loginEndpoint = async ({ username, password }: {username: string, password: string}) : Promise<IUser | null> => {
	try {
		const callable = httpsCallable(getFunctions(), 'loginUser')
		const result = await callable({ username, password })
		const { user, token } = result.data as { user: IUser, token: string }

		// Replaces the anonymous session with one whose uid is the users/{docId},
		// which is what weddings.createdBy already stores.
		await signInWithCustomToken(auth, token)

		return user
	} catch (error) {
		console.error('Login failed:', error)
		return null
	}
}

export const createNewWedding = async (wedding: IWedding) => {
	try {
		const data: IWedding = wedding

		await setDoc(doc(firestoreDb, 'weddings', wedding.id), data)
	} catch (e) {
		alert(`${e}, \n Javite se administratoru!`)
	}
}

export const fetchUserWeddings = async (user: IUser, allWeddings: boolean = false) => {
	let q

	if (user.isSuperAdmin && allWeddings) {
		q = query(
			collection(firestoreDb, 'weddings')
		)
	} else {
		q = query(
			collection(firestoreDb, 'weddings'),
			where('createdBy', '==', user.id)
		)
	}

	const userWeddings : IWedding[] = []

	try {
		const querySnapshot = await getDocs(q)

		querySnapshot.forEach((doc) => {
			const docData = doc.data() as IWedding

			const weddingData: IWeddingData = {
				brideName: docData.data.brideName,
				database: docData.data.database,
				dateEnd: toISOString(docData.data.dateEnd),
				dateStart: toISOString(docData.data.dateStart),
				groomName: docData.data.groomName,
				imageLimit: docData.data.imageLimit,
				surname: docData.data.surname,
				videoAvailable: docData.data.videoAvailable,
				language: docData.data.language
			}

			const wedding: IWedding = {
				id: doc.id,
				currentUsers: docData.currentUsers,
				maxUsers: docData.maxUsers,
				weddingPin: docData.weddingPin,
				data: weddingData,
				createdBy: docData.createdBy,
				packageOption: docData.packageOption
			}

			userWeddings.push(wedding)
		})

		return userWeddings
	} catch (error) {
		console.error('Error getting documents: ', error)
		alert(`${error}, \n Javite se administratoru!`)
	}

	return userWeddings
}

export const updateWedding = async (wedding: IWedding) => {
	try {
		const weddingRef = doc(firestoreDb, 'weddings', wedding.id)
		await updateDoc(weddingRef, { ...wedding })
	} catch (e) {
		alert(`${e}, \n Javite se administratoru!`)
	}
}

export const fetchUsers = async (): Promise<IUser[]> => {
	try {
		const callable = httpsCallable(getFunctions(), 'listPartners')
		const result = await callable({})
		return (result.data as { users: IUser[] }).users
	} catch (error) {
		console.error('Error fetching users:', error)
		alert(`${error}, \n Javite se administratoru!`)
		return []
	}
}

export const fetchUserData = async ({ userId }: {userId: string}) : Promise<IUser | null> => {
	try {
		const callable = httpsCallable(getFunctions(), 'getUserData')
		const result = await callable({ userId })
		return (result.data as { user: IUser }).user
	} catch (error) {
		console.error('Error fetching user data:', error)
		return null
	}
}

// ============ WEDDING PLANNER ENDPOINTS ============

// Both planner doors end up here, so they build the identical object from
// the identical document and the two screens cannot drift apart. The
// `planners` document is already flat (no nested `.data`), so there is no
// structure to rebuild the way the old wedding-shaped object needed.
const toPlanner = (id: string, docData: any): IPlanner => ({
	id,
	brideName: docData.brideName,
	groomName: docData.groomName,
	weddingDate: toISOString(docData.weddingDate),
	language: docData.language
})

// Reads the one planner a session is scoped to. A plain get by id, which is
// all firestore.rules grants a caller carrying a plannerFor claim on
// `planners` — the PIN is never part of a browser query any more.
export const fetchPlanner = async (plannerId: string): Promise<IPlanner | null> => {
	try {
		const snap = await getDoc(doc(firestoreDb, 'planners', plannerId))
		if (!snap.exists()) return null

		return toPlanner(snap.id, snap.data())
	} catch (error) {
		console.error('Error fetching planner:', error)
		return null
	}
}

// The pin-only door. This used to be a browser-side
// `where('plannerPin', '==', pin)` query, which could find the wedding but
// could not prove anything about the caller to the rules — so the old
// weddings/{id}/plannerData subcollection had to stay open to everyone.
// plannerPinLogin does the same lookup with the Admin SDK and hands back a
// token carrying a plannerFor claim, which is a statement only the server
// can make.
export const loginWithPlannerPin = async (pin: string): Promise<IPlanner | null> => {
	try {
		const callable = httpsCallable(getFunctions(), 'plannerPinLogin')
		const result = await callable({ pin })
		const { plannerId, token } = result.data as { plannerId: string, token: string }

		// Before the read, not after: the read below and every `planners`
		// call that follows it are only permitted under this session.
		await signInWithCustomToken(auth, token)

		return await fetchPlanner(plannerId)
	} catch (error) {
		console.error('Error logging in with planner PIN:', error)
		return null
	}
}

export const registerPlannerLead = async (input: {
	email: string,
	weddingDate: string,
	brideName: string,
	groomName: string,
	consent: boolean,
	language: string
}): Promise<{ ok: boolean, email: string }> => {
	const callable = httpsCallable(getFunctions(), 'registerPlannerUser')
	const result = await callable(input)
	return result.data as { ok: boolean, email: string }
}

export const verifyPlannerPinEndpoint = async (
	email: string,
	pin: string
): Promise<{ plannerId: string }> => {
	const callable = httpsCallable(getFunctions(), 'verifyPlannerPin')
	const result = await callable({ email, pin })
	const { plannerId, token } = result.data as { plannerId: string, token: string }

	// The same session the pin-only door mints. Without it the register page
	// would navigate into the planner holding nothing but an id, and every
	// `planners` read behind it would be denied.
	//
	// Deliberately not caught: a rejected PIN has to keep reaching the
	// register page's catch, which is what keeps the user on the PIN step.
	await signInWithCustomToken(auth, token)

	return { plannerId }
}

// ============ GUEST ENDPOINTS ============
// Guests are stored in: planners/{plannerId}/guests/{guestId}

export const fetchPlannerGuests = async (plannerId: string): Promise<IGuest[]> => {
	const guestsCollection = collection(firestoreDb, 'planners', plannerId, 'guests')

	const guests: IGuest[] = []

	try {
		const querySnapshot = await getDocs(guestsCollection)

		querySnapshot.forEach((docSnap) => {
			const data = docSnap.data()
			guests.push({
				id: docSnap.id,
				plannerId: plannerId,
				firstName: data.firstName,
				lastName: data.lastName,
				rsvpStatus: data.rsvpStatus || 'pending',
				tableId: data.tableId,
				seatPosition: data.seatPosition,
				notes: data.notes,
				// Bez ovog retka gost koji se sam upisao izgleda kao svaki drugi:
				// ovo je jedini put kojim gosti dolaze u Redux, pa značka i gumb
				// "Odobri" u popisu gostiju nikad ne dobiju svoj uvjet.
				addedByGuest: data.addedByGuest,
				createdAt: toISOString(data.createdAt),
				updatedAt: toISOString(data.updatedAt)
			})
		})

		return guests
	} catch (error) {
		console.error('Error fetching guests:', error)
		return []
	}
}

export const createGuest = async (guest: Omit<IGuest, 'id' | 'createdAt' | 'updatedAt'>): Promise<string | null> => {
	try {
		const guestsCollection = collection(firestoreDb, 'planners', guest.plannerId, 'guests')

		// Build document data, excluding undefined values (Firestore doesn't accept undefined)
		const docData: Record<string, any> = {
			firstName: guest.firstName,
			lastName: guest.lastName,
			rsvpStatus: guest.rsvpStatus,
			createdAt: serverTimestamp(),
			updatedAt: serverTimestamp()
		}

		// Only add optional fields if they have values
		if (guest.tableId !== undefined) docData.tableId = guest.tableId
		if (guest.seatPosition !== undefined) docData.seatPosition = guest.seatPosition
		if (guest.notes !== undefined && guest.notes !== '') docData.notes = guest.notes

		const docRef = await addDoc(guestsCollection, docData)
		return docRef.id
	} catch (error) {
		console.error('Error creating guest:', error)
		return null
	}
}

export const updateGuestEndpoint = async (guestId: string, updates: Partial<IGuest>, plannerId?: string): Promise<boolean> => {
	try {
		// plannerId is required to locate the guest in the subcollection
		if (!plannerId) {
			console.error('plannerId is required for updating guest')
			return false
		}
		const guestRef = doc(firestoreDb, 'planners', plannerId, 'guests', guestId)
		const { id, plannerId: _, createdAt, ...safeUpdates } = updates as any

		// Filter out undefined values (Firestore doesn't accept undefined)
		// Use null to explicitly delete a field
		const filteredUpdates: Record<string, any> = { updatedAt: serverTimestamp() }
		for (const [key, value] of Object.entries(safeUpdates)) {
			if (value === null) {
				filteredUpdates[key] = deleteField()
			} else if (value !== undefined) {
				filteredUpdates[key] = value
			}
		}

		await updateDoc(guestRef, filteredUpdates)
		return true
	} catch (error) {
		console.error('Error updating guest:', error)
		return false
	}
}

export const deleteGuestEndpoint = async (guestId: string, plannerId: string): Promise<boolean> => {
	try {
		await deleteDoc(doc(firestoreDb, 'planners', plannerId, 'guests', guestId))
		return true
	} catch (error) {
		console.error('Error deleting guest:', error)
		return false
	}
}

// ============ SEATING PLAN ENDPOINTS ============
// Seating plan is stored in: planners/{plannerId}/seatingPlan/plan (single
// document at a fixed id, not a collection — so it can be read without a query)

export const fetchSeatingPlan = async (plannerId: string): Promise<ISeatingPlan | null> => {
	const seatingPlanRef = doc(firestoreDb, 'planners', plannerId, 'seatingPlan', 'plan')

	try {
		const docSnap = await getDoc(seatingPlanRef)

		if (!docSnap.exists()) {
			// Create a default seating plan if none exists
			const defaultPlan = {
				tables: [],
				floorDimensions: { width: 1000, height: 800 },
				createdAt: serverTimestamp(),
				updatedAt: serverTimestamp()
			}

			await setDoc(seatingPlanRef, defaultPlan)

			return {
				id: 'plan',
				plannerId,
				tables: [],
				floorDimensions: { width: 1000, height: 800 },
				createdAt: new Date().toISOString(),
				updatedAt: new Date().toISOString()
			}
		}

		const data = docSnap.data()

		return {
			id: 'plan',
			plannerId: plannerId,
			tables: data.tables || [],
			floorDimensions: data.floorDimensions || { width: 1000, height: 800 },
			createdAt: toISOString(data.createdAt),
			updatedAt: toISOString(data.updatedAt)
		}
	} catch (error) {
		console.error('Error fetching seating plan:', error)
		return null
	}
}

// Helper function to remove undefined values from objects (Firebase doesn't accept undefined)
const removeUndefinedValues = (obj: any): any => {
	if (obj === null || obj === undefined) return obj
	if (Array.isArray(obj)) {
		return obj.map(item => removeUndefinedValues(item))
	}
	if (typeof obj === 'object') {
		const cleaned: any = {}
		for (const [key, value] of Object.entries(obj)) {
			if (value !== undefined) {
				cleaned[key] = removeUndefinedValues(value)
			}
		}
		return cleaned
	}
	return obj
}

export const updateSeatingPlanEndpoint = async (plannerId: string, updates: Partial<ISeatingPlan>): Promise<boolean> => {
	try {
		const seatingPlanRef = doc(firestoreDb, 'planners', plannerId, 'seatingPlan', 'plan')
		const { id, plannerId: _, createdAt, ...safeUpdates } = updates as any
		// Clean undefined values from the updates (Firebase doesn't accept undefined)
		const cleanedUpdates = removeUndefinedValues(safeUpdates)
		await setDoc(seatingPlanRef, {
			...cleanedUpdates,
			updatedAt: serverTimestamp()
		}, { merge: true })
		return true
	} catch (error) {
		console.error('Error updating seating plan:', error)
		return false
	}
}

// ============ RSVP UPITI ============
// Gosti koje automatski matcher nije mogao razriješiti (više osoba s istim
// imenom na popisu) - planners/{plannerId}/rsvpUpiti/{id}. Pravila dopuštaju
// čitanje i brisanje samo sesiji koja drži plannerFor claim tog planera.

export interface IRsvpUpit {
	id: string;
	ime: string;
	prezime: string;
}

export const fetchRsvpUpiti = async (plannerId: string): Promise<IRsvpUpit[]> => {
	const upiti = collection(firestoreDb, 'planners', plannerId, 'rsvpUpiti')
	const snapshot = await getDocs(upiti)
	return snapshot.docs.map((d) => ({ id: d.id, ...d.data() } as IRsvpUpit))
}

// Razriješen upit par miče s popisa sam. Briše se izravno s klijenta, istim
// putem kojim se i čita - pravilo na rsvpUpiti dopušta delete istoj PIN sesiji,
// pa za ovo ne treba callable. Bez ovoga popis samo raste.
export const deleteRsvpUpit = async (plannerId: string, upitId: string): Promise<void> => {
	await deleteDoc(doc(firestoreDb, 'planners', plannerId, 'rsvpUpiti', upitId))
}

// ============ POZIVNICA ============
// Digitalna pozivnica koju par ispisuje i preuzima za tisak, spremljena u
// planners/{plannerId}/pozivnica/podaci. Obred i domjenak su oba neobavezna
// (par možda drži samo obred), pa se dokument gradi kroz vise sjedenja, a
// nijedno polje koje par jos nije popunio ne smije blokirati spremanje onoga
// sto jest.

const toPozivnicaDogadjaj = (data: any): IPozivnicaDogadjaj | undefined => {
	if (!data) return undefined

	const dogadjaj: IPozivnicaDogadjaj = {}
	if (data.naziv !== undefined) dogadjaj.naziv = data.naziv
	if (data.mjesto !== undefined) dogadjaj.mjesto = data.mjesto
	if (data.adresa !== undefined) dogadjaj.adresa = data.adresa
	if (data.vrijeme !== undefined) dogadjaj.vrijeme = toISOString(data.vrijeme)

	return dogadjaj
}

// Čita fiksni dokument, isti obrazac kao fetchSeatingPlan - jedan dokument na
// poznatom putu, bez upita.
export const fetchPozivnica = async (plannerId: string): Promise<IPozivnicaPodaci | null> => {
	try {
		const snap = await getDoc(doc(firestoreDb, 'planners', plannerId, 'pozivnica', 'podaci'))
		if (!snap.exists()) return null

		const data = snap.data()

		return {
			obred: toPozivnicaDogadjaj(data.obred),
			domjenak: toPozivnicaDogadjaj(data.domjenak),
			poruka: data.poruka,
			rokPotvrde: data.rokPotvrde !== undefined ? toISOString(data.rokPotvrde) : undefined,
			stil: data.stil
		}
	} catch (error) {
		console.error('Error fetching pozivnica:', error)
		return null
	}
}

// Spljošti ugniježđeni objekt u putanje s točkom ("obred.naziv"), preskačući
// undefined vrijednosti. setDoc(..., { merge: true }) sa običnim ugniježđenim
// objektom zamijeni cijelu mapu ("obred") umjesto da je spoji - putanje s
// točkom su dokumentirani nacin da se dirne samo list koji je zapravo
// popunjen. Bez ovoga, spremanje samo obreda bi izbrisalo vec spremljeni
// domjenak iz ranijeg sjedenja.
const flattenPozivnica = (obj: Record<string, any>, prefix = ''): Record<string, any> => {
	const flat: Record<string, any> = {}

	for (const [key, value] of Object.entries(obj)) {
		if (value === undefined) continue

		const path = prefix ? `${prefix}.${key}` : key

		if (value !== null && typeof value === 'object' && !(value instanceof Date)) {
			Object.assign(flat, flattenPozivnica(value, path))
		} else {
			flat[path] = value
		}
	}

	return flat
}

export const savePozivnica = async (plannerId: string, updates: IPozivnicaPodaci): Promise<boolean> => {
	try {
		const pozivnicaRef = doc(firestoreDb, 'planners', plannerId, 'pozivnica', 'podaci')

		// vrijeme/rokPotvrde stižu kao ISO stringovi (isti oblik kao ostatak
		// endpointa); Firestore SDK sam pretvara pravi Date objekt u Timestamp
		// pri pisanju.
		const prepared: Record<string, any> = { ...updates }
		if (updates.obred) {
			prepared.obred = {
				...updates.obred,
				...(updates.obred.vrijeme !== undefined ? { vrijeme: new Date(updates.obred.vrijeme) } : {})
			}
		}
		if (updates.domjenak) {
			prepared.domjenak = {
				...updates.domjenak,
				...(updates.domjenak.vrijeme !== undefined ? { vrijeme: new Date(updates.domjenak.vrijeme) } : {})
			}
		}
		if (updates.rokPotvrde !== undefined) {
			prepared.rokPotvrde = new Date(updates.rokPotvrde)
		}

		const flatUpdates = flattenPozivnica(prepared)
		await setDoc(pozivnicaRef, flatUpdates, { merge: true })
		return true
	} catch (error) {
		console.error('Error saving pozivnica:', error)
		return false
	}
}

// Callable koji generira PDF (kartica ili A5 pozivnica) na serveru i vraca ga
// kao base64. Namjerno bez try/catch - odbijanje mora stici do komponente da
// par vidi da preuzimanje nije uspjelo, umjesto da tiho ne napravi nista.
export const pozivnicaPdfEndpoint = async (
	plannerId: string,
	oblik: 'kartica' | 'pozivnica'
): Promise<{ pdfBase64: string, naziv: string }> => {
	const callable = httpsCallable(getFunctions(), 'pozivnicaPdf')
	const result = await callable({ plannerId, oblik })
	return result.data as { pdfBase64: string, naziv: string }
}
